import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/hooks/useTenant";
import { Button } from "@/components/ui/button";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavLink } from "@/components/NavLink";
import {
  Shield,
  Building2,
  CreditCard,
  Brain,
  ScrollText,
  Settings,
  LogOut,
  KeyRound,
  Apple,
} from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { title: "Academias", url: "/admin", icon: Building2 },
  { title: "Planos", url: "/admin/planos", icon: CreditCard },
  { title: "Acessos", url: "/admin/acessos", icon: KeyRound },
  { title: "Nutrição Revenue", url: "/admin/nutricao-revenue", icon: Apple },
  { title: "Métricas de IA", url: "/admin/ai-usage", icon: Brain },
  { title: "Logs de Auditoria", url: "/admin/logs", icon: ScrollText },
  { title: "Configurações", url: "/admin/config", icon: Settings },
];

function OwnerSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();

  const isActive = (url: string) => {
    if (url === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(url);
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border/60">
      <SidebarContent>
        {/* Brand */}
        <div className={cn("flex items-center gap-3 px-4 py-4 border-b border-border/40", collapsed && "justify-center px-2")}>
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          {!collapsed && (
            <div>
              <h1 className="text-sm font-bold text-foreground leading-tight">PersonAI</h1>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Admin</p>
            </div>
          )}
        </div>

        <SidebarGroup>
          <SidebarGroupLabel>Plataforma</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === "/admin"}
                      className="hover:bg-muted/50"
                      activeClassName="bg-primary/10 text-primary font-medium"
                    >
                      <item.icon className="mr-2 h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

export default function OwnerLayout() {
  const { signOut } = useAuth();
  const { isPlatformOwner } = useTenant();

  if (!isPlatformOwner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <Shield className="h-12 w-12 mx-auto text-destructive" />
          <h1 className="text-xl font-bold text-foreground">Acesso Negado</h1>
          <p className="text-muted-foreground">Você não tem permissão para acessar esta área.</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <OwnerSidebar />
        <div className="flex-1 flex flex-col">
          <header className="h-12 flex items-center justify-between border-b border-border/60 px-4">
            <SidebarTrigger className="ml-0" />
            <Button
              variant="ghost"
              size="sm"
              onClick={signOut}
              className="h-8 px-2 text-muted-foreground hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </header>
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
